// src/components/Reports/TrailRunOffReportPage.tsx
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { db } from '../../lib/firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  QueryConstraint,
  doc,
  updateDoc
} from 'firebase/firestore';
import { formatCurrency, formatCurrencyShorthand, formatLoanBook } from '../../utils/formatters';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { Activity, AlertTriangle, Siren, HeartPulse, TrendingUp, AlertCircle, DollarSign, Layers } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { SkeletonReportPage } from '../../components/common/SkeletonLoader';
import { Spinner } from '../../components/common/Spinner';
import { HelpButton } from '../../components/help/HelpButton';
import { cardHelp } from '../../config/helpContent';
import { useTrailRunOffPercentage } from '../../hooks/useTrailRunOffPercentage';
import TrailBookChangeStatCard from '../../components/reports/TrailBookChangeStatCard';
import { useTrailRunOffPortfolio } from '../../hooks/useTrailRunOffPortfolio';
import { useHideCommissions } from '../../hooks/useHideCommissions';

// Type definitions
type SortField = 'name' | 'previousAverage' | 'recentAverage' | 'dropPercentage';
type LiveSortField = 'broker' | 'previousTotal' | 'currentTotal' | 'dropAmount' | 'dropPct';
type SortDirection = 'asc' | 'desc';

interface UserData {
  uid: string;
  companyId: string;
  displayName: string;
  role: string;
  email: string;
  canSeeAllOffices?: boolean;
}

interface CompanyData {
  id: string;
  trailRunOffSettings?: TrailRunOffSettings;
}

interface TrailRunOffSettings {
  isEnabled: boolean;
  lookbackMonths: number;
  thresholdPct: number;
  notifyEmails: string[];
  topN: number;
  filterPaymentType?: string | string[];
}

interface BrokerData {
  name: string;
  previousAverage: number;
  recentAverage: number;
  dropPercentage: number;
}

interface BrokerDrop {
  broker: string;
  previousTotal: number;
  currentTotal: number;
  dropAmount: number;
  dropPct: number;
}

interface TrailRunOffReport {
  id: string;
  type: string;
  createdAt: Timestamp;
  settings?: TrailRunOffSettings;
  brokers: BrokerData[];
  brokerNames: string[];
  metrics?: {
    lookbackMonths: number;
    previousTotal: number;
    currentTotal: number;
    changePct: number;
  };
}

interface AnalysisResult {
  triggered: boolean;
  topDroppers: BrokerDrop[];
  metrics: {
    lookbackMonths: number;
    previousWindowStart: string;
    currentWindowStart: string;
    currentWindowEnd: string;
    previousTotal: number;
    currentTotal: number;
    changePct: number;
  };
}

interface OutletContextType {
  userData: UserData | null;
  companyData: CompanyData | null;
}

const TrailRunOffReportPage: React.FC = () => {
  const { userData, companyData } = useOutletContext<OutletContextType>();
  const hideCommissions = useHideCommissions();

  // Always-on portfolio metrics for the stat cards (independent of Run Now)
  // This page has no loan-book / broker filter of its own, so the card stays
  // company-wide here; the Loan Trail Drop-Off page passes its filters through.
  const {
    runOffPercentage,
    health: runOffHealth,
    bookChange: runOffBookChange,
    periodLabel: runOffPeriodLabel,
    loading: runOffLoading,
  } = useTrailRunOffPercentage({ companyId: userData?.companyId || '' });
  const {
    totalLineOfTrailCents,
    activeTrailBalanceCents,
    inactiveTrailBalanceCents,
    totalLoansWithTrail,
    totalTrailCommissionCents,
    changes: portfolioChanges,
    periodLabel: portfolioPeriodLabel,
    loading: portfolioLoading,
  } = useTrailRunOffPortfolio({ companyId: userData?.companyId || '' });

  const [reports, setReports] = useState<TrailRunOffReport[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [selectedReport, setSelectedReport] = useState<TrailRunOffReport | null>(null);

  // Instant Analysis State
  const [liveAnalysis, setLiveAnalysis] = useState<AnalysisResult | null>(null);
  const [analyzing, setAnalyzing] = useState<boolean>(false);
  const [analysisError, setAnalysisError] = useState<string>('');

  // Sorting state for historical reports table
  const [sortField, setSortField] = useState<SortField>('dropPercentage');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Sorting state for live analysis table
  const [liveSortField, setLiveSortField] = useState<LiveSortField>('dropPct');
  const [liveSortDirection, setLiveSortDirection] = useState<SortDirection>('desc');

  // Broker name lookup map (broker_id -> display_name)
  const [brokerNameMap, setBrokerNameMap] = useState<Map<string, string>>(new Map());

  // Settings State
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [settings, setSettings] = useState<TrailRunOffSettings>({
    isEnabled: false,
    lookbackMonths: 3,
    thresholdPct: 25,
    notifyEmails: [],
    topN: 10,
  });
  const [savingSettings, setSavingSettings] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<string>('');
  const [emailsInput, setEmailsInput] = useState<string>('');

  // Load company settings
  useEffect(() => {
    if (companyData?.trailRunOffSettings) {
      setSettings(companyData.trailRunOffSettings);
      // Load email list
      const emails = companyData.trailRunOffSettings.notifyEmails;
      if (Array.isArray(emails) && emails.length > 0) {
        setEmailsInput(emails.join(', '));
      } else {
        setEmailsInput('');
      }
    }
  }, [companyData]);

  // Load broker name lookup map (admin-only: getBrokersList enforces super_admin/administrator)
  useEffect(() => {
    if (!userData?.companyId) return;
    // Skip for roles that don't have access to getBrokersList
    if (!['super_admin', 'administrator'].includes(userData.role)) return;

    const fetchBrokers = async () => {
      try {
        const getBrokersList = httpsCallable(functions, 'getBrokersList');
        const result = await getBrokersList({ companyId: userData.companyId });
        const data = result.data as { brokers: { broker_id: string; broker_name: string }[] };

        const nameMap = new Map<string, string>();
        for (const broker of data.brokers) {
          nameMap.set(broker.broker_id, broker.broker_name);
        }
        setBrokerNameMap(nameMap);
      } catch (err) {
        console.error('[TrailRunOffReportPage] Failed to load brokers:', err);
        // Non-fatal: we'll fall back to displaying the stored name
      }
    };

    fetchBrokers();
  }, [userData?.companyId, userData?.role]);

  // Load historical reports (super_admin only — these are company-wide scheduled reports)
  useEffect(() => {
    if (!userData?.companyId) {
      setLoading(false);
      setError('Company information not available');
      return;
    }

    // Historical reports are COMPANY-WIDE aggregates (both the scheduled
    // checkTrailRunOff job and the manual runTrailRunOffAnalysis persist only
    // unscoped runs). Only users with company-wide data access should see them:
    // super_admin always qualifies; administrator qualifies iff canSeeAllOffices
    // is explicitly true. office_admin and scope-limited administrators are
    // excluded because surfacing company-wide broker drop data would leak
    // other offices' brokers beyond their permitted scope. Firestore rules
    // enforce the same gate; this client check is UX defense in depth.
    const hasCompanyWideAccess =
      userData.role === 'super_admin' ||
      (userData.role === 'administrator' && userData.canSeeAllOffices === true);
    if (!hasCompanyWideAccess) {
      setLoading(false);
      setReports([]);
      return;
    }

    // Commission-visibility gate: report docs contain dollar amounts. Skip the
    // listener for users with hideCommissions=true — Firestore rules also deny
    // these reads, so calling .onSnapshot would just trigger permission-denied
    // errors. The live "Run Analysis Now" path still strips financials in the
    // callable response for these users.
    if (hideCommissions) {
      setLoading(false);
      setReports([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Build query constraints
      const constraints: QueryConstraint[] = [
        where("type", "==", "TRAIL_RUN_OFF"),
        orderBy('createdAt', 'desc'),
        limit(50)
      ];

      const reportsQuery = query(
        collection(db, `companies/${userData.companyId}/reports`),
        ...constraints
      );

      const unsubscribe = onSnapshot(
        reportsQuery,
        (snapshot) => {
          try {
            const fetchedReports = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data()
            } as TrailRunOffReport));

            setReports(fetchedReports);

            // Auto-select first report if none selected
            if (fetchedReports.length > 0 && !selectedReport) {
              setSelectedReport(fetchedReports[0]);
            }

            setLoading(false);
            setError('');
          } catch (err) {
            console.error('[TrailRunOffReportPage] Error processing reports:', err);
            setError("Error processing report data");
            setLoading(false);
          }
        },
        (err) => {
          console.error('[TrailRunOffReportPage] Error fetching reports:', err);
          setError("Could not load reports. You may not have permission or no reports are available.");
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      console.error('[TrailRunOffReportPage] Error setting up reports listener:', err);
      setError("Failed to initialize reports");
      setLoading(false);
    }
  }, [userData, selectedReport, hideCommissions]);

  // Run instant analysis
  const runAnalysis = async () => {
    if (!userData?.companyId) {
      setAnalysisError('Company information not available');
      return;
    }

    setAnalyzing(true);
    setAnalysisError('');
    setLiveAnalysis(null);

    try {
      const runTrailRunOffAnalysis = httpsCallable(functions, 'runTrailRunOffAnalysis');

      // Settings come from Firestore (companyData.trailRunOffSettings), so a legacy
      // doc can hold a string or a decimal. The callable takes whole numbers only.
      const wholeNumber = (value: unknown, fallback: number): number => {
        const n = Math.round(Number(value));
        return Number.isFinite(n) ? n : fallback;
      };
      const result = await runTrailRunOffAnalysis({
        companyId: userData.companyId,
        lookbackMonths: wholeNumber(settings.lookbackMonths, 3),
        thresholdPct: wholeNumber(settings.thresholdPct, 25),
        topN: wholeNumber(settings.topN, 10),
      });

      setLiveAnalysis(result.data as AnalysisResult);
      setAnalyzing(false);
    } catch (err: any) {
      console.error('[TrailRunOffReportPage] Analysis failed:', err);
      setAnalysisError(err.message || 'Failed to run analysis');
      setAnalyzing(false);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    if (!companyData?.id) {
      setAnalysisError('Company information not available');
      return;
    }

    setSavingSettings(true);
    setSaveSuccess('');
    setAnalysisError('');

    try {
      // Parse and validate emails
      const emailArray = emailsInput
        .split(',')
        .map((e) => e.trim())
        .filter((e) => e.length > 0);

      // Validate email formats
      if (emailArray.length > 0) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidEmails = emailArray.filter(email => !emailRegex.test(email));
        if (invalidEmails.length > 0) {
          throw new Error(`Invalid email address: ${invalidEmails[0]}`);
        }
      }

      const updatedSettings: TrailRunOffSettings = {
        ...settings,
        notifyEmails: emailArray
      };

      const companyRef = doc(db, 'companies', companyData.id);
      await updateDoc(companyRef, {
        trailRunOffSettings: updatedSettings
      });

      setSaveSuccess('Settings saved successfully!');
      setTimeout(() => setSaveSuccess(''), 3000);
    } catch (err: any) {
      console.error('[TrailRunOffReportPage] Failed to save settings:', err);
      setAnalysisError('Failed to save settings: ' + (err.message || 'Unknown error'));
    } finally {
      setSavingSettings(false);
    }
  };

  const formatDate = (timestamp: Timestamp | null | undefined): string => {
    if (!timestamp?.toDate) return 'N/A';
    return timestamp.toDate().toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Handle column sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to descending for new field
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Get sorted brokers for selected report
  const getSortedBrokers = (brokers: BrokerData[] | undefined): BrokerData[] => {
    if (!brokers) return [];
    return [...brokers].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      switch (sortField) {
        case 'name':
          // Resolve broker names before sorting (handles broker IDs)
          aVal = (a.name.startsWith('b_') ? brokerNameMap.get(a.name) || a.name : a.name).toLowerCase();
          bVal = (b.name.startsWith('b_') ? brokerNameMap.get(b.name) || b.name : b.name).toLowerCase();
          break;
        case 'previousAverage':
          aVal = a.previousAverage;
          bVal = b.previousAverage;
          break;
        case 'recentAverage':
          aVal = a.recentAverage;
          bVal = b.recentAverage;
          break;
        case 'dropPercentage':
          aVal = a.dropPercentage;
          bVal = b.dropPercentage;
          break;
        default:
          return 0;
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Sort indicator component for historical reports
  const SortIndicator = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <span className="ml-1 text-gray-500">↕</span>;
    }
    return <span className="ml-1">{sortDirection === 'asc' ? '▲' : '▼'}</span>;
  };

  // Handle live analysis column sorting
  const handleLiveSort = (field: LiveSortField) => {
    if (liveSortField === field) {
      setLiveSortDirection(liveSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setLiveSortField(field);
      setLiveSortDirection('desc');
    }
  };

  // Get sorted live analysis droppers
  const getSortedLiveDroppers = (droppers: BrokerDrop[]): BrokerDrop[] => {
    return [...droppers].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      switch (liveSortField) {
        case 'broker':
          aVal = a.broker.toLowerCase();
          bVal = b.broker.toLowerCase();
          break;
        case 'previousTotal':
          aVal = a.previousTotal;
          bVal = b.previousTotal;
          break;
        case 'currentTotal':
          aVal = a.currentTotal;
          bVal = b.currentTotal;
          break;
        case 'dropAmount':
          aVal = a.dropAmount;
          bVal = b.dropAmount;
          break;
        case 'dropPct':
          aVal = a.dropPct;
          bVal = b.dropPct;
          break;
        default:
          return 0;
      }
      if (aVal < bVal) return liveSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return liveSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Sort indicator component for live analysis
  const LiveSortIndicator = ({ field }: { field: LiveSortField }) => {
    if (liveSortField !== field) {
      return <span className="ml-1 text-gray-500">↕</span>;
    }
    return <span className="ml-1">{liveSortDirection === 'asc' ? '▲' : '▼'}</span>;
  };

  // Resolve broker name: try lookup map first, then fall back to stored name
  // This handles historical reports that stored broker IDs instead of display names
  const resolveBrokerName = (storedName: string): string => {
    // If it looks like a broker ID (starts with b_), try to resolve it
    if (storedName.startsWith('b_')) {
      return brokerNameMap.get(storedName) || storedName;
    }
    // Otherwise, it's already a display name
    return storedName;
  };

  if (loading) {
    return <SkeletonReportPage statCards={6} statColumns={3} tableColumns={6} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Activity}
        title="Broker Trail Run-Off"
        subtitle="Analyze broker trail commission trends and detect significant drops instantly"
        actions={<HelpButton pageKey="trailRunOff" />}
      />

      {/*
        Portfolio stat cards, always on, rolling 12 months.
        Responsive grid per docs/UI_UX_STANDARDS.md Rule 3 (7 cards):
        1-up mobile → 2-up tablet → 4-up desktop (renders as 4+3). Money values
        are compact to match the dashboard stat cards: loan balances use
        formatLoanBook (as Total Loan Book on the dashboard), commission uses
        formatCurrencyShorthand (as Upfront/Trail Commission there). The exact
        figure is in each value's tooltip (valueTitle). Counts stay full.

        Period-over-period change chips on each card use 'vs prev 12mo' delta
        from useTrailRunOffPortfolio. Two cards are inverse-semantic (lower
        is better): Portfolio Run-Off Rate and Inactive Loan Balance.
      */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Portfolio Run-Off Rate"
          value={runOffPercentage !== null ? `${runOffPercentage.toFixed(1)}%` : '--'}
          periodLabel={runOffPeriodLabel}
          subtitle={
            runOffPercentage !== null
              ? runOffHealth === 'critical'
                ? '>12% — needs attention'
                : runOffHealth === 'warning'
                  ? '8–12% — monitor'
                  : '<8% — healthy'
              : undefined
          }
          icon={
            runOffHealth === 'critical' ? <Siren size={24} /> :
            runOffHealth === 'warning' ? <AlertTriangle size={24} /> :
            <HeartPulse size={24} />
          }
          gradientColor={
            runOffHealth === 'critical' ? '#ef4444' :
            runOffHealth === 'warning' ? '#f59e0b' :
            '#22c55e'
          }
          isUpdating={runOffLoading}
          helpPageKey="trailRunOff"
          helpKey="portfolioRunOffRate"
          helpTooltip={cardHelp('trailRunOff', 'portfolioRunOffRate')}
          change={
            portfolioChanges.runOffPercentagePctPoints !== null
              ? { value: portfolioChanges.runOffPercentagePctPoints, unit: 'pp', isInverse: true, label: 'vs prev 12mo' }
              : null
          }
        />
        <TrailBookChangeStatCard change={runOffBookChange} periodLabel={runOffPeriodLabel} loading={runOffLoading} />
        <StatCard
          title="Total Loan Book on Trail"
          value={totalLineOfTrailCents !== null ? formatLoanBook(totalLineOfTrailCents) : '--'}
          valueTitle={totalLineOfTrailCents !== null ? formatCurrency(totalLineOfTrailCents) : undefined}
          periodLabel={portfolioPeriodLabel}
          subtitle="Balance with trail activity"
          icon={<TrendingUp size={24} />}
          gradientColor="#3b82f6"
          isUpdating={portfolioLoading}
          helpPageKey="trailRunOff"
          helpKey="totalLoanBookOnTrail"
          helpTooltip={cardHelp('trailRunOff', 'totalLoanBookOnTrail')}
          change={
            portfolioChanges.totalLineOfTrailPct !== null
              ? { value: portfolioChanges.totalLineOfTrailPct, label: 'vs prev 12mo' }
              : null
          }
        />
        <StatCard
          title="Active Loan Balance"
          value={activeTrailBalanceCents !== null ? formatLoanBook(activeTrailBalanceCents) : '--'}
          valueTitle={activeTrailBalanceCents !== null ? formatCurrency(activeTrailBalanceCents) : undefined}
          periodLabel={portfolioPeriodLabel}
          subtitle="Paid in last 6 months"
          icon={<HeartPulse size={24} />}
          gradientColor="#10b981"
          isUpdating={portfolioLoading}
          helpPageKey="trailRunOff"
          helpKey="activeLoanBalance"
          helpTooltip={cardHelp('trailRunOff', 'activeLoanBalance')}
          change={
            portfolioChanges.activeTrailBalancePct !== null
              ? { value: portfolioChanges.activeTrailBalancePct, label: 'vs prev 12mo' }
              : null
          }
        />
        <StatCard
          title="Inactive Loan Balance"
          value={inactiveTrailBalanceCents !== null ? formatLoanBook(inactiveTrailBalanceCents) : '--'}
          valueTitle={inactiveTrailBalanceCents !== null ? formatCurrency(inactiveTrailBalanceCents) : undefined}
          periodLabel={portfolioPeriodLabel}
          subtitle="No payment in 6+ months"
          icon={<AlertCircle size={24} />}
          gradientColor="#f59e0b"
          isUpdating={portfolioLoading}
          helpPageKey="trailRunOff"
          helpKey="inactiveLoanBalance"
          helpTooltip={cardHelp('trailRunOff', 'inactiveLoanBalance')}
          change={
            portfolioChanges.inactiveTrailBalancePct !== null
              ? { value: portfolioChanges.inactiveTrailBalancePct, isInverse: true, label: 'vs prev 12mo' }
              : null
          }
        />
        <StatCard
          title="Annual Trail Revenue"
          value={totalTrailCommissionCents !== null ? formatCurrencyShorthand(totalTrailCommissionCents) : '--'}
          valueTitle={totalTrailCommissionCents !== null ? formatCurrency(totalTrailCommissionCents) : undefined}
          periodLabel={portfolioPeriodLabel}
          subtitle="Trail commission paid (12 months)"
          icon={<DollarSign size={24} />}
          gradientColor="#8b5cf6"
          isUpdating={portfolioLoading}
          helpPageKey="trailRunOff"
          helpKey="annualTrailRevenue"
          helpTooltip={cardHelp('trailRunOff', 'annualTrailRevenue')}
          change={
            portfolioChanges.totalTrailCommissionPct !== null
              ? { value: portfolioChanges.totalTrailCommissionPct, label: 'vs prev 12mo' }
              : null
          }
        />
        <StatCard
          title="Loans Paying Trail"
          value={totalLoansWithTrail !== null ? totalLoansWithTrail.toLocaleString('en-AU') : '--'}
          periodLabel={portfolioPeriodLabel}
          subtitle="Distinct loans (12 months)"
          icon={<Layers size={24} />}
          gradientColor="#06b6d4"
          isUpdating={portfolioLoading}
          helpPageKey="trailRunOff"
          helpKey="loansPayingTrail"
          helpTooltip={cardHelp('trailRunOff', 'loansPayingTrail')}
          change={
            portfolioChanges.totalLoansWithTrailPct !== null
              ? { value: portfolioChanges.totalLoansWithTrailPct, label: 'vs prev 12mo' }
              : null
          }
        />
      </div>

      {/* Settings Panel */}
      <div className="bg-gray-800 p-6 rounded-lg shadow-lg mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Analysis Settings</h3>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="text-blue-400 hover:text-blue-300 transition-colors"
          >
            {showSettings ? 'Hide Settings' : 'Show Settings'}
          </button>
        </div>

        {showSettings && (
          <div className="space-y-6 mt-6 pt-6 border-t border-gray-700">
            {/* Enable Alerts Toggle */}
            <div className="flex items-center justify-between pb-6 border-b border-gray-700">
              <div>
                <h4 className="text-base font-semibold text-white">Enable Automated Alerts</h4>
                <p className="text-sm text-gray-400 mt-1">
                  Turn on/off the automated weekly email reports for trail run-off detection.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.isEnabled}
                  onChange={(e) => setSettings({ ...settings, isEnabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Lookback Months */}
            <div className={settings.isEnabled ? '' : 'opacity-50 pointer-events-none'}>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Lookback Period (months)
              </label>
              <select
                value={settings.lookbackMonths}
                onChange={(e) => setSettings({ ...settings, lookbackMonths: parseInt(e.target.value) })}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3 focus:border-blue-500 focus:outline-none"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>{n} month{n > 1 ? 's' : ''}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Compare last N months vs previous N months
              </p>
            </div>

            {/* Threshold Percentage */}
            <div className={settings.isEnabled ? '' : 'opacity-50 pointer-events-none'}>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Run-Off Threshold ({settings.thresholdPct}%)
              </label>
              <input
                type="range"
                min="10"
                max="50"
                step="1"
                value={settings.thresholdPct}
                onChange={(e) => setSettings({ ...settings, thresholdPct: parseInt(e.target.value) })}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
              <p className="text-xs text-gray-500 mt-2">
                Alert threshold for trail commission drop percentage
              </p>
            </div>

            {/* Top N Brokers */}
            <div className={settings.isEnabled ? '' : 'opacity-50 pointer-events-none'}>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Top {settings.topN} Brokers to Display
              </label>
              <select
                value={settings.topN}
                onChange={(e) => setSettings({ ...settings, topN: parseInt(e.target.value) })}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3 focus:border-blue-500 focus:outline-none"
              >
                {[5, 10, 15, 20, 25, 30, 40, 50].map(n => (
                  <option key={n} value={n}>{n} brokers</option>
                ))}
              </select>
            </div>

            {/* Notification Email Recipients */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Notification Recipients
              </label>
              <input
                type="text"
                value={emailsInput}
                onChange={(e) => setEmailsInput(e.target.value)}
                placeholder="manager@example.com, admin@example.com"
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3 focus:border-blue-500 focus:outline-none"
              />
              <p className="text-xs text-gray-500 mt-2">
                Enter email addresses separated by commas. Each will receive alerts when thresholds are exceeded.
              </p>
            </div>

            {/* Save Settings Button */}
            <div className="pt-6 border-t border-gray-700">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
                >
                  {savingSettings ? 'Saving...' : 'Save Settings'}
                </button>
                {saveSuccess && (
                  <span className="text-green-400 text-sm">{saveSuccess}</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Run Analysis Button */}
      <div className="mb-8">
        <button
          onClick={runAnalysis}
          disabled={analyzing}
          className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors text-lg font-semibold flex items-center gap-3"
        >
          {analyzing ? (
            <>
              <Spinner size="sm" />
              Analyzing...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Run Analysis Now
            </>
          )}
        </button>
        {analysisError && (
          <p className="text-red-400 mt-2 text-sm">{analysisError}</p>
        )}
      </div>

      {/* Live Analysis Results */}
      {liveAnalysis && (
        <div className="bg-gray-800 rounded-lg shadow-lg mb-8">
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold">Live Analysis Results</h3>
              <div className={`px-4 py-2 rounded-md text-sm font-semibold ${
                liveAnalysis.triggered ? 'bg-red-900 text-red-300' : 'bg-green-900 text-green-300'
              }`}>
                {liveAnalysis.triggered ? 'Alert Triggered' : 'Normal'}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Previous {liveAnalysis.metrics.lookbackMonths}-Month Total</p>
                <p className="text-2xl font-bold">{formatCurrency(liveAnalysis.metrics.previousTotal)}</p>
              </div>
              <div>
                <p className="text-gray-400">Recent {liveAnalysis.metrics.lookbackMonths}-Month Total</p>
                <p className="text-2xl font-bold">{formatCurrency(liveAnalysis.metrics.currentTotal)}</p>
              </div>
              <div>
                <p className="text-gray-400">Change</p>
                <p className={`text-2xl font-bold ${liveAnalysis.metrics.changePct < 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {liveAnalysis.metrics.changePct.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            {liveAnalysis.topDroppers.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                No trail commission drops detected in the analysis period.
              </div>
            ) : (
              <table className="w-full text-sm text-left text-gray-400">
                <thead className="text-xs text-gray-400 uppercase bg-gray-700">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 cursor-pointer hover:text-white transition-colors select-none"
                      onClick={() => handleLiveSort('broker')}
                    >
                      Broker<LiveSortIndicator field="broker" />
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 cursor-pointer hover:text-white transition-colors select-none"
                      onClick={() => handleLiveSort('previousTotal')}
                    >
                      Prev. {liveAnalysis.metrics.lookbackMonths}-Month Total<LiveSortIndicator field="previousTotal" />
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 cursor-pointer hover:text-white transition-colors select-none"
                      onClick={() => handleLiveSort('currentTotal')}
                    >
                      Recent {liveAnalysis.metrics.lookbackMonths}-Month Total<LiveSortIndicator field="currentTotal" />
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 cursor-pointer hover:text-white transition-colors select-none"
                      onClick={() => handleLiveSort('dropAmount')}
                    >
                      Drop Amount<LiveSortIndicator field="dropAmount" />
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 cursor-pointer hover:text-white transition-colors select-none"
                      onClick={() => handleLiveSort('dropPct')}
                    >
                      Drop %<LiveSortIndicator field="dropPct" />
                    </th>
                    <th scope="col" className="px-6 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedLiveDroppers(liveAnalysis.topDroppers).map((broker, index) => {
                    const exceedsThreshold = broker.dropPct >= settings.thresholdPct;
                    return (
                      <tr
                        key={`${broker.broker}-${index}`}
                        className={`border-b border-gray-700 hover:bg-gray-750 ${
                          exceedsThreshold ? 'bg-red-900 bg-opacity-20' : ''
                        }`}
                      >
                        <td className="px-6 py-4 font-medium text-white">{broker.broker}</td>
                        <td className="px-6 py-4">{formatCurrency(broker.previousTotal)}</td>
                        <td className="px-6 py-4">{formatCurrency(broker.currentTotal)}</td>
                        <td className="px-6 py-4 text-red-400">{formatCurrency(broker.dropAmount)}</td>
                        <td className="px-6 py-4">
                          <span className={`font-semibold ${exceedsThreshold ? 'text-red-400' : 'text-orange-400'}`}>
                            {broker.dropPct.toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {exceedsThreshold ? (
                            <span className="text-red-400">🔴 Alert</span>
                          ) : (
                            <span className="text-green-400">✅ Normal</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Historical Alerts Section */}
      {reports.length > 0 && (
        <div className="mt-12">
          <button
            onClick={() => setSelectedReport(selectedReport ? null : reports[0])}
            className="text-xl font-semibold text-blue-400 hover:text-blue-300 transition-colors mb-4"
          >
            {selectedReport ? '▼ Historical Alerts' : '▶ Historical Alerts'} ({reports.length})
          </button>

          {selectedReport && (
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-1/3 bg-gray-800 p-4 rounded-lg">
                <h3 className="font-semibold mb-4 px-2">Generated Reports</h3>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {reports.map((report) => (
                    <button
                      key={report.id}
                      onClick={() => setSelectedReport(report)}
                      className={`w-full text-left px-4 py-3 rounded-md transition-colors text-sm ${
                        selectedReport?.id === report.id
                          ? 'bg-blue-600 text-white'
                          : 'hover:bg-gray-700'
                      }`}
                    >
                      <p className="font-semibold">
                        Report - {formatDate(report.createdAt)}
                      </p>
                      <p className="text-xs text-gray-400">
                        {report.brokers?.length || 0} broker(s) triggered
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="w-full md:w-2/3">
                <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                  <div className="p-6 border-b border-gray-700">
                    <h3 className="text-xl font-bold">Report Details</h3>
                    <p className="text-gray-400">
                      Generated on {formatDate(selectedReport.createdAt)}
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-400">
                      <thead className="text-xs text-gray-400 uppercase bg-gray-700">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-3 cursor-pointer hover:text-white transition-colors select-none"
                            onClick={() => handleSort('name')}
                          >
                            Broker<SortIndicator field="name" />
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 cursor-pointer hover:text-white transition-colors select-none"
                            onClick={() => handleSort('previousAverage')}
                          >
                            Prev. {selectedReport.settings?.lookbackMonths || 3}-Month Avg.<SortIndicator field="previousAverage" />
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 cursor-pointer hover:text-white transition-colors select-none"
                            onClick={() => handleSort('recentAverage')}
                          >
                            Recent {selectedReport.settings?.lookbackMonths || 3}-Month Avg.<SortIndicator field="recentAverage" />
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 cursor-pointer hover:text-white transition-colors select-none"
                            onClick={() => handleSort('dropPercentage')}
                          >
                            Percentage Drop<SortIndicator field="dropPercentage" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getSortedBrokers(selectedReport.brokers).map((broker, index) => (
                          <tr
                            key={`${broker.name}-${index}`}
                            className="bg-gray-800 border-b border-gray-700 hover:bg-gray-750"
                          >
                            <td className="px-6 py-4 font-medium text-white">
                              {resolveBrokerName(broker.name)}
                            </td>
                            <td className="px-6 py-4">
                              {formatCurrency(broker.previousAverage)}
                            </td>
                            <td className="px-6 py-4">
                              {formatCurrency(broker.recentAverage)}
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-red-400 font-semibold">
                                {broker.dropPercentage}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* No Reports Message */}
      {reports.length === 0 && !liveAnalysis && (
        <div className="bg-gray-800 p-12 rounded-lg shadow-lg text-center">
          <h3 className="text-xl font-semibold mb-2">No Historical Reports</h3>
          <p className="text-gray-400 mb-6">
            The automated system has not detected any significant trail run-offs yet.
          </p>
          <p className="text-gray-500 text-sm">
            Click "Run Analysis Now" above to perform an instant analysis with your current settings.
          </p>
        </div>
      )}
    </div>
  );
};

export default TrailRunOffReportPage;
